import Slate from 'slate';
import {
  Serializer,
  Deserializer,
  State,
  BLOCKS,
} from '@wikifactory/markup-it';
import markupHTML from '@wikifactory/markup-it/lib/html';
import parse from 'libs/editor/serializers/htmlParser';
import { videoInfoToURL } from 'libs/editor/views/mediaInput';

import escapeHTML from '@wikifactory/markup-it/lib/html/escape';
import trimTrailingLines from 'trim-trailing-lines';

const previewBlockSerialize = Serializer()
  .matchType('preview-block')
  .then(state => {
    const node = state.peek();
    const href = node.data.get('href');
    const alt = node.data.get('alt');
    const text = `<p><a href="${href}">${alt}</a></p>`;
    return state.shift().write(text);
  });

const previewSerialize = Serializer()
  .matchType('preview')
  .then(state => {
    const node = state.peek();
    const href = node.data.get('href');
    const alt = node.data.get('alt');
    const text = `<a href="${href}">${alt}</a>`;
    return state.shift().write(text);
  });

const linkifiedLinkSerialize = Serializer()
  .matchType('linkified-link')
  .then(state => {
    const node = state.peek();
    return state.shift().write(node.text);
  });

const imageBlockSerialize = Serializer()
  .matchType('image-block')
  .then(state => {
    const node = state.peek();
    const src = node.data.get('src');
    const alt = node.data.get('alt');
    const text = `<p><img src="${src}" alt="${alt}"/></p>`;
    return state.shift().write(text);
  });

const videoBlockSerialize = Serializer()
  .matchType('video-block')
  .then(state => {
    const node = state.peek();
    const src = node.data.get('src');
    const site = node.data.get('site');
    const text = `<p><iframe src="${videoInfoToURL(
      src,
      site,
    )}" site="${site}" frameborder="0" allowfullscreen></iframe></p>`;
    return state.shift().write(text);
  });

const videoSerialize = Serializer()
  .matchType('video')
  .then(state => {
    const node = state.peek();
    const src = node.data.get('src');
    const site = node.data.get('site');
    const text = `<iframe src="${videoInfoToURL(
      src,
      site,
    )}" site="${site}" frameborder="0" allowfullscreen></iframe>`;
    return state.shift().write(text);
  });

const deserialize = Deserializer().then(state => {
  const { text } = state;
  const document = parse(text);
  return state.skip(text.length).push(document);
});

const serializeListItem = Serializer()
  .matchType(BLOCKS.LIST_ITEM)
  .then(state => {
    const node = state.peek();
    let inner = state.serialize(node.nodes);
    let className = '';

    const isTaskList = node.data.has('checked');
    const isChecked = node.data.get('checked');

    if (isTaskList) {
      className = ' class="task-list-item"';
      inner = `<input type="checkbox" class="task-list-item-checkbox"${
        isChecked ? ' checked' : ''
      } disabled /> ${inner}`;
    }

    return state.shift().write(`<li${className}>${inner}</li>`);
  });

function containsTaskList(list) {
  const { nodes } = list;
  return nodes.some(item => item.data.has('checked'));
}

const serializeList = Serializer()
  .matchType([BLOCKS.OL_LIST, BLOCKS.UL_LIST])
  .then(state => {
    const node = state.peek();
    const tag = node.type === BLOCKS.OL_LIST ? 'ol' : 'ul';
    const isTaskList = containsTaskList(node);
    const inner = state.serialize(node.nodes);

    return state
      .shift()
      .write(
        `<${tag}${
          isTaskList ? ' class="contains-task-list"' : ''
        }>${inner}</${tag}>`,
      );
  });

const codeBlockSerialize = Serializer()
  .matchType(BLOCKS.CODE)
  .then(state => {
    const node = state.peek();
    const syntax = node.data.get('syntax');
    const text = node.nodes.map(line => line.text).join('\n');

    const className = syntax ? ` class="lang-${syntax}"` : '';

    return state
      .shift()
      .write(`<pre><code${className}>${escapeHTML(text)}</code></pre>`);
  });

markupHTML.block.unshift({ serialize: codeBlockSerialize });
markupHTML.block.unshift({ serialize: linkifiedLinkSerialize });
markupHTML.block.unshift({ serialize: previewBlockSerialize });
markupHTML.block.unshift({ serialize: previewSerialize });
markupHTML.block.unshift({ serialize: imageBlockSerialize });
markupHTML.block.unshift({ serialize: videoBlockSerialize });
markupHTML.block.unshift({ serialize: videoSerialize });
markupHTML.block.unshift({ serialize: serializeListItem });
markupHTML.block.unshift({ serialize: serializeList });
markupHTML.document[0].deserialize = deserialize;
const parser = State.create(markupHTML);

const emptyText = {
  object: 'text',
  leaves: [
    {
      object: 'leaf',
      text: '',
      marks: [],
    },
  ],
};

const fastNormalizeText = node => {
  if (['block', 'inline'].includes(node.object)) {
    if (!node.nodes.length) {
      node.nodes = [{ ...emptyText }];
      return node;
    }
    if (
      ![
        'code_block',
        'blockquote',
        'unordered_list',
        'ordered_list',
        'list_item',
      ].includes(node.type)
    ) {
      if (node.nodes[0].object !== 'text') {
        node.nodes = [{ ...emptyText }, ...node.nodes];
      }
      if (node.nodes[node.nodes.length - 1].object !== 'text') {
        node.nodes.push({ ...emptyText });
      }
    }
  }

  if (node.nodes) {
    // special cases: some inlines => blocks
    if (
      node.type === 'paragraph' &&
      node.nodes.length === 3 &&
      node.nodes[0].object === 'text' &&
      node.nodes[0].leaves.length === 1 &&
      !node.nodes[0].leaves[0].text &&
      node.nodes[1].object === 'inline' &&
      ['image', 'video', 'preview'].includes(node.nodes[1].type) &&
      node.nodes[2].object === 'text' &&
      node.nodes[2].leaves.length === 1 &&
      !node.nodes[2].leaves[0].text
    ) {
      const inlineNode = node.nodes[1];
      node.object = 'block';
      node.type = `${inlineNode.type}-block`;
      node.nodes = [{ ...emptyText }];
      node.data = inlineNode.data;
      return node;
    }
    const newNodes = [];
    let currentText = null;
    node.nodes.forEach((child, i) => {
      if (child.object === 'text') {
        if (currentText) {
          currentText.leaves.push(...child.leaves);
        } else {
          currentText = child;
        }
        if (i === node.nodes.length - 1) {
          newNodes.push(currentText);
        }
      } else {
        if (currentText) {
          newNodes.push(currentText);
          currentText = null;
        }
        newNodes.push(fastNormalizeText(child));
      }
    });
    node.nodes = newNodes;
  }
  return node;
};

export const html = {
  deserialize: content => {
    content = trimTrailingLines(content);
    const document = fastNormalizeText(
      parser.deserializeToDocument(content).toJSON(),
    );
    return Slate.Value.fromJSON({ document });
  },
  serialize: value => {
    const inputDocument = value.document;
    const out = parser.serializeDocument(inputDocument);
    return out;
  },
};
